import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";

// ─────────────────────────────────────────────────────────────
// Logo marks — each returns an inline SVG. Color is `currentColor`
// so the same mark works in white / black / signal-red contexts.
// ─────────────────────────────────────────────────────────────

const Mark01 = ({ size = 96, accent = "#FF3333" }) => (
  // Crosshair mark — the "A" becomes a precision target.
  // Concept: eye-tracking / measurement focus.
  <svg width={size} height={size} viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="2" y="2" width="96" height="96" stroke="currentColor" strokeWidth="2" />
    <line x1="50" y1="14" x2="50" y2="38" stroke="currentColor" strokeWidth="2" />
    <line x1="50" y1="62" x2="50" y2="86" stroke="currentColor" strokeWidth="2" />
    <line x1="14" y1="50" x2="38" y2="50" stroke="currentColor" strokeWidth="2" />
    <line x1="62" y1="50" x2="86" y2="50" stroke="currentColor" strokeWidth="2" />
    <circle cx="50" cy="50" r="14" stroke="currentColor" strokeWidth="2" />
    <circle cx="50" cy="50" r="5" fill={accent} />
  </svg>
);

const Mark02 = ({ size = 96, accent = "#FF3333" }) => (
  // Bracketed monogram — "[CATT]" suggesting a test boundary / evaluation frame.
  <svg width={size} height={size} viewBox="0 0 140 100" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* left bracket */}
    <path d="M14 20 L4 20 L4 80 L14 80" stroke="currentColor" strokeWidth="3" fill="none" />
    {/* right bracket */}
    <path d="M126 20 L136 20 L136 80 L126 80" stroke="currentColor" strokeWidth="3" fill="none" />
    {/* CATT wordmark */}
    <text
      x="70"
      y="64"
      textAnchor="middle"
      fontFamily="'Cabinet Grotesk', sans-serif"
      fontWeight="800"
      fontSize="34"
      letterSpacing="-1"
      fill="currentColor"
    >
      CATT
    </text>
    {/* signal dot */}
    <circle cx="70" cy="78" r="2.5" fill={accent} />
  </svg>
);

const Mark03 = ({ size = 96, accent = "#FF3333" }) => (
  // 2×2 grid monogram — each letter in its own quadrant. Brutalist precision.
  <svg width={size} height={size} viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="2" y="2" width="96" height="96" stroke="currentColor" strokeWidth="2" />
    <line x1="50" y1="2" x2="50" y2="98" stroke="currentColor" strokeWidth="2" />
    <line x1="2" y1="50" x2="98" y2="50" stroke="currentColor" strokeWidth="2" />
    <text x="26" y="38" textAnchor="middle" fontFamily="'Cabinet Grotesk', sans-serif" fontWeight="800" fontSize="26" fill="currentColor">C</text>
    <text x="74" y="38" textAnchor="middle" fontFamily="'Cabinet Grotesk', sans-serif" fontWeight="800" fontSize="26" fill="currentColor">A</text>
    <text x="26" y="80" textAnchor="middle" fontFamily="'Cabinet Grotesk', sans-serif" fontWeight="800" fontSize="26" fill="currentColor">T</text>
    <text x="74" y="80" textAnchor="middle" fontFamily="'Cabinet Grotesk', sans-serif" fontWeight="800" fontSize="26" fill="currentColor">T</text>
    {/* signal dot in centre */}
    <circle cx="50" cy="50" r="3.5" fill={accent} />
  </svg>
);

const Mark04 = ({ size = 96, accent = "#FF3333" }) => (
  // C-aperture — the letter C as an iris/aperture with a focus dot.
  // Concept: observation, attention, the act of watching users.
  <svg width={size} height={size} viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="50" cy="50" r="44" stroke="currentColor" strokeWidth="3" fill="none" />
    {/* opening at right (the C gap) */}
    <path d="M50 6 A44 44 0 1 0 50 94" stroke="currentColor" strokeWidth="3" fill="none" strokeLinecap="square" />
    {/* inner ring */}
    <circle cx="50" cy="50" r="22" stroke="currentColor" strokeWidth="1.5" fill="none" />
    {/* focal dot */}
    <circle cx="50" cy="50" r="7" fill={accent} />
    {/* corner ticks */}
    <line x1="6" y1="6" x2="14" y2="6" stroke="currentColor" strokeWidth="1.5" />
    <line x1="6" y1="6" x2="6" y2="14" stroke="currentColor" strokeWidth="1.5" />
    <line x1="94" y1="94" x2="86" y2="94" stroke="currentColor" strokeWidth="1.5" />
    <line x1="94" y1="94" x2="94" y2="86" stroke="currentColor" strokeWidth="1.5" />
  </svg>
);

const Mark05 = ({ size = 96, accent = "#FF3333" }) => (
  // Stacked typographic mark — tall geometric "CATT" with signal underline.
  <svg width={size * 1.4} height={size} viewBox="0 0 140 100" fill="none" xmlns="http://www.w3.org/2000/svg">
    <text
      x="70"
      y="62"
      textAnchor="middle"
      fontFamily="'Cabinet Grotesk', sans-serif"
      fontWeight="800"
      fontSize="60"
      letterSpacing="-3"
      fill="currentColor"
    >
      CATT
    </text>
    {/* underline with two tick marks */}
    <line x1="14" y1="80" x2="126" y2="80" stroke="currentColor" strokeWidth="2" />
    <line x1="14" y1="76" x2="14" y2="84" stroke="currentColor" strokeWidth="2" />
    <line x1="126" y1="76" x2="126" y2="84" stroke="currentColor" strokeWidth="2" />
    {/* signal dot midpoint */}
    <circle cx="70" cy="80" r="3" fill={accent} />
  </svg>
);

// ─────────────────────────────────────────────────────────────

const LOGOS = [
  {
    id: "01",
    name: "Crosshair",
    rationale:
      "A precision target inside a square frame — the central red dot is the user's fixation point. Evokes eye-tracking, attention, and the empirical core of usability evaluation.",
    Mark: Mark01,
    tagOrder: "Most clinical · best for instrumentation contexts",
  },
  {
    id: "02",
    name: "Bracket Lockup",
    rationale:
      "[CATT] in square brackets — the brackets read as a test boundary, a code expression, and a citation. Works beautifully as a horizontal lockup on letterhead.",
    Mark: Mark02,
    tagOrder: "Editorial / academic",
  },
  {
    id: "03",
    name: "Quadrant Monogram",
    rationale:
      "Each letter occupies its own quadrant with a signal dot at the intersection — a four-domain mark (AI · Robotics · Medical · HCI) collapsed into one mark. Most ownable.",
    Mark: Mark03,
    tagOrder: "Most ownable, scales well to favicon",
  },
  {
    id: "04",
    name: "Aperture",
    rationale:
      "A C-shaped aperture with concentric rings and a focal dot — the act of observation itself. Slightly more conceptual; reads well at large scale.",
    Mark: Mark04,
    tagOrder: "Lab signage / merchandise",
  },
  {
    id: "05",
    name: "Wordmark",
    rationale:
      "A pure typographic mark — tight Cabinet Grotesk caps with a measurement-style underline and signal dot. The most flexible, works without supporting imagery.",
    Mark: Mark05,
    tagOrder: "Default everyday wordmark",
  },
];

export default function LogoProposals() {
  return (
    <main data-testid="logo-page" className="relative bg-[#101012] min-h-screen text-white">
      <Nav />

      {/* Header */}
      <section className="relative pt-32 lg:pt-40 pb-16 border-b border-white/10 overflow-hidden">
        <div className="absolute inset-0 grid-lines opacity-50" />
        <div className="relative max-w-[1440px] mx-auto px-6 lg:px-10">
          <Link
            to="/"
            data-testid="logo-back"
            className="inline-flex items-center gap-2 font-mono text-[10px] tracking-widest uppercase text-white/60 hover:text-white transition-colors mb-12"
          >
            <ArrowLeft size={14} /> Back to home
          </Link>

          <div className="grid grid-cols-12 gap-6 items-end">
            <div className="col-span-12 lg:col-span-8">
              <div className="label-mono mb-4">// Logo · Five Concepts</div>
              <motion.h1
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7 }}
                className="display-1 text-4xl md:text-6xl lg:text-7xl"
              >
                Five marks.
                <br />
                <span className="text-white/40">One</span>{" "}
                <span className="text-[#FF3333]">CATT.</span>
              </motion.h1>
            </div>
            <div className="col-span-12 lg:col-span-4">
              <p className="text-white/70 text-base leading-relaxed">
                Each proposal is a single-color vector that works on dark and light surfaces,
                scales to a favicon, and pairs with the Cabinet Grotesk wordmark. Pick a
                direction and we'll refine it.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Logo grid */}
      <section className="border-b border-white/10">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-16 grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10">
          {LOGOS.map((l, i) => {
            const M = l.Mark;
            return (
              <motion.article
                key={l.id}
                data-testid={`logo-card-${l.id}`}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                className="bg-[#101012] p-10 lg:p-14 flex flex-col"
              >
                <div className="flex items-start justify-between mb-10">
                  <div className="font-mono text-[10px] tracking-widest uppercase text-[#FF3333]">
                    Concept · {l.id}
                  </div>
                  <div className="font-mono text-[10px] tracking-widest uppercase text-white/40">
                    {l.tagOrder}
                  </div>
                </div>

                {/* Mark — three contexts: dark / light / accent */}
                <div className="grid grid-cols-3 gap-px bg-white/10 border border-white/10">
                  <div className="bg-[#101012] aspect-square flex items-center justify-center text-white">
                    <M size={110} />
                  </div>
                  <div className="bg-white aspect-square flex items-center justify-center text-[#101012]">
                    <M size={110} accent="#FF3333" />
                  </div>
                  <div className="bg-[#FF3333] aspect-square flex items-center justify-center text-white">
                    <M size={110} accent="#101012" />
                  </div>
                </div>

                <h3 className="font-display font-bold text-3xl text-white tracking-tight mt-8">
                  {l.name}
                </h3>
                <p className="mt-3 text-white/65 text-sm leading-relaxed">{l.rationale}</p>

                {/* Inline wordmark lockup */}
                <div className="mt-8 pt-6 border-t border-white/10 flex items-center gap-4 text-white">
                  <M size={44} />
                  <div className="flex flex-col leading-none">
                    <span className="font-display font-bold text-lg tracking-tight">CATT</span>
                    <span className="font-mono text-[9px] tracking-[0.22em] text-white/40 uppercase mt-1">
                      Center / Adv. Tech. & Testing
                    </span>
                  </div>
                </div>
              </motion.article>
            );
          })}
        </div>
      </section>

      {/* Notes */}
      <section className="border-b border-white/10 py-20">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10 grid grid-cols-12 gap-6">
          <div className="col-span-12 lg:col-span-4">
            <div className="label-mono mb-4">// Notes</div>
            <h3 className="font-display font-bold text-3xl text-white">
              How to choose.
            </h3>
          </div>
          <ul className="col-span-12 lg:col-span-7 space-y-5 text-white/75 text-base leading-relaxed">
            <li className="flex gap-5 border-t border-white/10 pt-5">
              <span className="font-mono text-[10px] tracking-widest uppercase text-[#FF3333] w-10 pt-1">01</span>
              <span>
                <strong className="text-white">Reduces well.</strong> Pick the mark that still reads at 16×16 (favicon).
                Concept 03 (Quadrant) and 01 (Crosshair) reduce best.
              </span>
            </li>
            <li className="flex gap-5 border-t border-white/10 pt-5">
              <span className="font-mono text-[10px] tracking-widest uppercase text-[#FF3333] w-10 pt-1">02</span>
              <span>
                <strong className="text-white">Tells the right story.</strong> Crosshair / Aperture speak loudest to
                "we measure attention". Bracket / Wordmark speak loudest to "we publish evidence".
              </span>
            </li>
            <li className="flex gap-5 border-t border-white/10 pt-5">
              <span className="font-mono text-[10px] tracking-widest uppercase text-[#FF3333] w-10 pt-1">03</span>
              <span>
                <strong className="text-white">Lives everywhere.</strong> All five are single-color vectors. They will
                emboss on lab badges, foil on letterhead, and stand alone on a poster.
              </span>
            </li>
          </ul>
        </div>
      </section>

      <Footer />
    </main>
  );
}
